/**
 * Copyright 2015-present Singlepoint. All Rights Reserved.
 *
 * @flow
 */

import '../PolicyLinksPage.scss';

import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import type { RouterHistory } from 'react-router-dom';

import AnimatedSpinner from '../../../components/AnimatedSpinner/AnimatedSpinner';
import { ExternalLinkWrapper } from '../../../components/ExternalLink/ExternalLink';
import { HOME_INSURANCE_TYPE } from '../../../constants/insuranceTypeConstants';
import { localPhoneNumber } from '../../../constants/phoneNumbers';
import { POLICY_RENEWAL } from '../../../constants/sessionStorage/genericStorageIdentifiers';
import { getObjectFromSessionStorage } from '../../../helpers';
import { getGenericDocumentLinks } from '../../../services/common/commonServices';
import { getHomeDocumentLinks } from '../../../services/homeServices';
import type {
  GetPolicyDocumentResponse,
  PolicyDocumentModel
} from '../../../types/commonTypes';
import type { AppliedDocumentGroup } from '../../../types/Home';
import DocumentsCard from './DocumentsCard';
import type { StaticLink } from './policyLinks';

const className = 'c-PolicyLinksPage';

interface Props {
  history: RouterHistory;
  insuranceType: string;
  staticLinks: StaticLink[];
}

const LinksPage = ({ history, insuranceType, staticLinks = [] }: Props) => {
  const [documentLinks, setDocumentLinks] = useState<PolicyDocumentModel[]>([]);
  const [loadingDocumentLinks, setLoadingDocumentLinks] = useState(true);
  const [policyDocs, setPolicyDocs] = useState<AppliedDocumentGroup[]>([]);

  useEffect(() => {
    const policy =
      getObjectFromSessionStorage(POLICY_RENEWAL, false) ||
      getObjectFromSessionStorage(`${insuranceType}PolicyNumber`);

    const eventualData =
      insuranceType === HOME_INSURANCE_TYPE
        ? groupedHomeDocuments(policy.policy_id).then(setPolicyDocs)
        : genericDocumentLinks(policy).then(setDocumentLinks);

    eventualData.finally(() => setLoadingDocumentLinks(false));
  }, []);

  const groupedHomeDocuments = (
    policyId: string
  ): Promise<AppliedDocumentGroup[]> =>
    getHomeDocumentLinks({
      business_processes: [
        'NEW_BUSINESS',
        'RENEWAL_CONFIRMATION',
        'RENEWAL_TRANSFER',
        'LAPSE'
      ],
      policy_id: policyId
    });

  // eslint-disable-next-line flowtype/no-weak-types
  const genericDocumentLinks = (policy: any): Promise<PolicyDocumentModel[]> =>
    getGenericDocumentLinks(
      insuranceType,
      policy?.reference_number || policy?.referenceNumber,
      history
    ).then((docs: GetPolicyDocumentResponse) =>
      docs?.policies?.length ? docs.policies[0].documents : []
    );

  const getStaticLinks = () => {
    return staticLinks.map((labelsAndUrl: StaticLink, index: number) => (
      <div
        className={`${className}__linksContainer`}
        key={`StaticLinks_${index}`}
      >
        <ExternalLinkWrapper
          url={labelsAndUrl.url}
          className={`${className}__link`}
        >
          {labelsAndUrl.label}
          <FontAwesomeIcon
            className={`${className}__linkIcon`}
            icon={faDownload}
          />
        </ExternalLinkWrapper>
      </div>
    ));
  };

  const homeLinks = () =>
    policyDocs.length ? (
      policyDocs.map((doc: AppliedDocumentGroup, index: number) => (
        <DocumentsCard
          documentsGroup={doc}
          className={className}
          expanded={index === 0}
          key={`docGroup_${index}`}
        />
      ))
    ) : (
      <div className={`${className}__noDocumentsMessage`}>
        Your purchased policy is still being processed. If your documents are
        not made available to you within the next 24 hours please call us on
        <a href={`tel:${localPhoneNumber}`} /> {localPhoneNumber}
      </div>
    );

  return (
    <>
      {loadingDocumentLinks ? (
        <div className={`${className}__spinnerContainer`}>
          <AnimatedSpinner size="large" />
        </div>
      ) : insuranceType === HOME_INSURANCE_TYPE ? (
        homeLinks()
      ) : (
        <>
          {getStaticLinks()}
          {documentLinks.length > 0 &&
            documentLinks.map(
              (document: PolicyDocumentModel, index: number) => {
                return (
                  <div
                    className={`${className}__linksContainer`}
                    key={`travel_document_${index}`}
                  >
                    <ExternalLinkWrapper
                      url={document.url}
                      className={`${className}__link`}
                    >
                      {document.name}
                      <FontAwesomeIcon
                        className={`${className}__linkIcon`}
                        icon={faDownload}
                      />
                    </ExternalLinkWrapper>
                  </div>
                );
              }
            )}
        </>
      )}
    </>
  );
};

export default LinksPage;
